import { useState } from 'react';
import { ExternalLink, Unlink, RotateCcw, ShieldAlert } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';

// Shows what the BDAF scan linked to a business for one signal, with owner "Not mine" / restore controls.
export function SignalEvidence({ signal, onDisconnect, onRestore }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const k = signal.key;

  if (signal.rejected) {
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-[10px] bg-amber-50 border border-[#FDE68A] px-3 py-2" data-testid={`signal-rejected-${k}`}>
        <ShieldAlert className="w-3.5 h-3.5 text-status-amber shrink-0" />
        <p className="font-body text-xs text-ink-2 flex-1 min-w-0">
          Scan result disconnected by you{signal.rejected.reason ? ` — “${signal.rejected.reason}”` : ''}
          {signal.rejected.evidence?.website_url && <span className="text-mid"> · was {signal.rejected.evidence.website_url}</span>}
        </p>
        <button onClick={async () => { setBusy(true); try { await onRestore(k); } finally { setBusy(false); } }} disabled={busy}
          className="inline-flex items-center gap-1 font-body text-xs font-semibold text-brand-orange hover:underline disabled:opacity-50" data-testid={`signal-restore-${k}`}>
          <RotateCcw className="w-3 h-3" /> Restore
        </button>
      </div>
    );
  }
  if (!signal.scan_detected) return null;

  const ev = signal.evidence || {};
  return (
    <div className="space-y-2" data-testid={`signal-evidence-${k}`}>
      <div className="flex flex-wrap items-center gap-2 rounded-[10px] bg-bg-warm px-3 py-2">
        <p className="font-body text-xs text-ink-2 flex-1 min-w-0 truncate">
          <span className="text-mid">BDAF scan linked: </span>
          {ev.url ? <a href={ev.url} target="_blank" rel="noreferrer" className="text-forest font-semibold hover:underline inline-flex items-center gap-1" data-testid={`signal-evidence-link-${k}`}>{ev.url.replace(/^https?:\/\//, '').slice(0, 60)} <ExternalLink className="w-3 h-3" /></a> : <span>{ev.detail}</span>}
          {ev.url && ev.detail && <span className="text-mid"> · {ev.detail}</span>}
        </p>
        {!open && (
          <button onClick={() => setOpen(true)} className="inline-flex items-center gap-1 font-body text-xs font-semibold text-status-red hover:underline" data-testid={`signal-disconnect-${k}`}>
            <Unlink className="w-3 h-3" /> Not mine — disconnect
          </button>
        )}
      </div>
      {open && (
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center pl-3" data-testid={`signal-disconnect-form-${k}`}>
          <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="Why is this wrong? (optional, e.g. belongs to a different company)" className="font-body text-xs h-8" data-testid={`signal-disconnect-reason-${k}`} />
          <div className="flex gap-2 shrink-0">
            <Button size="sm" variant="destructive" disabled={busy} className="rounded-full font-body text-xs h-8" data-testid={`signal-disconnect-confirm-${k}`}
              onClick={async () => { setBusy(true); try { await onDisconnect(k, reason); setOpen(false); setReason(''); } finally { setBusy(false); } }}>Confirm disconnect</Button>
            <Button size="sm" variant="ghost" className="rounded-full font-body text-xs h-8" onClick={() => setOpen(false)} data-testid={`signal-disconnect-cancel-${k}`}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}
